import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { processNoteContent, NOTE_PROSE_CLASSES } from '../utils/noteContent';

function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const confirmed = searchParams.get('confirmed') === 'true';
  const unsubscribed = searchParams.get('unsubscribed') === 'true';
  const error = searchParams.get('error');
  const [loading, setLoading] = useState(!!token && !confirmed);
  const [subscriber, setSubscriber] = useState(null);
  const [note, setNote] = useState(null);
  const [noteLoading, setNoteLoading] = useState(false);

  useEffect(() => {
    if (token && !confirmed && !error) {
      // Verify token to get subscriber info
      axios.get(`${API_BASE_URL}/mailing-list/verify-token/${token}`)
        .then(response => {
          if (response.data.valid && response.data.subscriber) {
            setSubscriber(response.data.subscriber);
          }
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [token, confirmed, error]);

  // Fetch note content when confirmed
  useEffect(() => {
    if (confirmed && !note && !noteLoading) {
      setNoteLoading(true);
      axios.get(`${API_BASE_URL}/note/mailing-list-thankyou`)
        .then(response => {
          if (response.data.success && response.data.data) {
            setNote(response.data.data);
          }
          setNoteLoading(false);
        })
        .catch(() => {
          setNoteLoading(false);
        });
    }
  }, [confirmed, note, noteLoading]);

  if (unsubscribed) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="mb-6">
              <svg
                className="w-20 h-20 mx-auto text-green-600 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Unsubscribed</h1>
              <p className="text-lg text-gray-600 mb-4">
                {error ? error : "You have been successfully unsubscribed from our mailing list."}
              </p>
            </div>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="mb-6">
              <svg
                className="w-20 h-20 mx-auto text-red-600 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription Error</h1>
              <p className="text-lg text-gray-600 mb-4">{error}</p>
            </div>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading || (confirmed && noteLoading)) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  // If confirmed and note is loaded, display the note content
  if (confirmed && note) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            {!note.hideTitle && (
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{note.title}</h1>
            )}
            <div
              className={NOTE_PROSE_CLASSES}
              dangerouslySetInnerHTML={{ __html: processNoteContent(note.content) }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="mb-6">
            <svg
              className="w-20 h-20 mx-auto text-green-600 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Thank You!</h1>
            {confirmed ? (
              <p className="text-lg text-gray-600 mb-4">
                Your subscription has been confirmed. You're now part of our mailing list!
              </p>
            ) : subscriber ? (
              <p className="text-lg text-gray-600 mb-4">
                Thank you for subscribing, {subscriber.firstName}! Please check your email to confirm your subscription.
              </p>
            ) : (
              <p className="text-lg text-gray-600 mb-4">
                Thank you for your interest! We'll be in touch soon.
              </p>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm">
              {confirmed
                ? "You'll receive updates about new designs, special offers, and more."
                : "Please check your email and click the confirmation link to complete your subscription."}
            </p>
          </div>

          <Link
            to="/"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ThankYouPage;
